import React, { useState, useEffect } from "react";
import "./ProductShowcase.css";

// ────────────────────────────────────────────────────────
// 1. MiniAuditPreview - Above the Fold (Hero Right Column)
// ────────────────────────────────────────────────────────
export const MiniAuditPreview = () => {
  const [progress, setProgress] = useState(0);
  const [score, setScore] = useState(35);
  const [phase, setPhase] = useState("init"); // init -> crawling -> analyzing -> complete

  useEffect(() => {
    // A simple self-repeating simulation of an audit running on a landing page
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setPhase((currentPhase) => {
            if (currentPhase === "init" || currentPhase === "crawling") {
              setScore(68);
              return "analyzing";
            }
            if (currentPhase === "analyzing") {
              setScore(85);
              return "complete";
            }
            if (currentPhase === "complete") {
              setScore(35);
              return "crawling"; // restart loop
            }
            return "crawling";
          });
          return 0;
        }
        return prev + 4;
      });
    }, 150);

    return () => clearInterval(timer);
  }, []);

  const getStatusText = () => {
    if (phase === "init") return "Initializing scanner...";
    if (phase === "crawling") return `Crawling pages... ${progress}%`;
    if (phase === "analyzing") return `Running 50+ SEO checks... ${progress}%`;
    return "Audit complete! Score compiled.";
  };

  return (
    <div className="mini-audit-preview-card">
      {/* Browser bar */}
      <div className="preview-browser-header">
        <div className="browser-dots">
          <span className="dot red"></span>
          <span className="dot yellow"></span>
          <span className="dot green"></span>
        </div>
        <div className="browser-address-bar">your-website.com/audit</div>
      </div>

      {/* Preview content */}
      <div className="preview-browser-body">
        <div className="preview-status-strip">
          <div className="scanner-indicator">
            <span className={`pulse-dot ${phase !== "complete" ? "active" : ""}`}></span>
            <span className="status-text">{getStatusText()}</span>
          </div>
          <div className={`progress-bar-container ${phase === "complete" ? "hidden-layout" : ""}`}>
            <div className="progress-bar-fill" style={{ width: phase === "complete" ? "100%" : `${progress}%` }}></div>
          </div>
        </div>

        <div className="preview-grid">
          <div className="preview-score-box">
            <div className="preview-score-circle">
              <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" className="circle-bg" />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  className="circle-fill"
                  style={{
                    strokeDasharray: "251",
                    strokeDashoffset: 251 - (251 * score) / 100,
                    stroke: score > 80 ? "var(--success)" : score > 60 ? "var(--warning)" : "var(--danger)",
                  }}
                />
              </svg>
              <div className="preview-score-value">{score}</div>
            </div>
            <div className="preview-score-label">SEO SCORE</div>
          </div>

          <div className="preview-quick-metrics">
            <div className="quick-metric-item">
              <span className="metric-icon">✓</span>
              <div className="metric-details">
                <span className="metric-title">SSL Security</span>
                <span className="metric-status success">Valid HTTPS</span>
              </div>
            </div>
            <div className="quick-metric-item">
              <span className="metric-icon">⚠</span>
              <div className="metric-details">
                <span className="metric-title">Meta Tags</span>
                <span className="metric-status warning">
                  {phase === "complete" ? "1 Missing Og Tag" : "Analyzing..."}
                </span>
              </div>
            </div>
            <div className="quick-metric-item">
              <span className="metric-icon">⚡</span>
              <div className="metric-details">
                <span className="metric-title">Core Web Vitals</span>
                <span className={`metric-status ${score > 80 ? "success" : "warning"}`}>
                  {phase === "complete" ? "LCP 2.4s (Good)" : "Measuring..."}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="preview-audit-log">
          <div className="log-title">Real-Time Audit Feeds</div>
          <div className="log-items">
            <div className="log-line complete">✓ Checked canonical tag formatting</div>
            <div className={`log-line ${phase === "crawling" ? "loading" : "complete"}`}>
              {phase === "crawling" ? "⏱ Crawling robots.txt and sitemap..." : "✓ robots.txt and sitemap loaded"}
            </div>
            <div className={`log-line ${phase === "init" || phase === "crawling" ? "pending" : "complete"}`}>
              {phase === "init" || phase === "crawling"
                ? "○ Waiting to analyze content structure..."
                : "✓ Header hierarchy checked (H1-H6)"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────
// 2. AuditReportMockup - Immediately Below the Fold
// ────────────────────────────────────────────────────────
export const AuditReportMockup = () => {
  return (
    <div className="audit-report-mockup-section">
      <div className="section-header">
        <div className="section-badge">Live Product Preview</div>
        <h2 className="section-title">Deep Technical Audits. Instant Fixes.</h2>
        <p className="section-subtitle">
          Don't settle for high-level advice. Get a comprehensive, issue-by-issue breakdown with clear developer instructions.
        </p>
      </div>

      <div className="mockup-container-outer">
        {/* Callout overlays pointing to specific sections of the report */}
        <div className="callout-pointer callout-meta" data-number="1">
          <div className="callout-badge">✓ Missing Meta Tags</div>
          <div className="callout-desc">
            Instantly audit page titles, meta descriptions, and Open Graph tags for optimal CTR.
          </div>
        </div>

        <div className="callout-pointer callout-links" data-number="2">
          <div className="callout-badge">✓ Broken Links</div>
          <div className="callout-desc">
            Identify 404 links, redirect chains, and broken anchor tags on your site in seconds.
          </div>
        </div>

        <div className="callout-pointer callout-perf" data-number="3">
          <div className="callout-badge">✓ Performance Issues</div>
          <div className="callout-desc">
            Break down Core Web Vitals (LCP, CLS, FID) with action items to boost mobile rankings.
          </div>
        </div>

        <div className="callout-pointer callout-schema" data-number="4">
          <div className="callout-badge">✓ Schema Problems</div>
          <div className="callout-desc">
            Validate JSON-LD structured data and find missing fields before Google reports them.
          </div>
        </div>

        {/* The Dashboard Report Frame */}
        <div className="report-dashboard-frame">
          {/* Browser Header */}
          <div className="report-browser-header">
            <div className="browser-dots">
              <span className="dot red"></span>
              <span className="dot yellow"></span>
              <span className="dot green"></span>
            </div>
            <div className="browser-address-bar">
              <span>https://</span>your-website.com/audit/report-0f8a92
            </div>
            <div className="browser-actions-placeholder">
              <span className="btn-action">Export PDF</span>
            </div>
          </div>

          {/* Dashboard Header Panel */}
          <div className="report-summary-bar">
            <div className="report-score-indicator">
              <div className="score-ring-wrapper">
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" className="ring-bg" />
                  <circle cx="50" cy="50" r="42" className="ring-fill" />
                </svg>
                <div className="score-value">85</div>
              </div>
              <div className="score-meta">
                <h3>Good SEO Profile</h3>
                <p>Last scanned 2 minutes ago • 54 checks performed</p>
              </div>
            </div>
            <div className="quick-totals">
              <div className="total-box red-glow">
                <span className="total-num text-danger">3</span>
                <span className="total-lbl">Critical Issues</span>
              </div>
              <div className="total-box yellow-glow">
                <span className="total-num text-warning">5</span>
                <span className="total-lbl">Warnings</span>
              </div>
              <div className="total-box green-glow">
                <span className="total-num text-success">46</span>
                <span className="total-lbl">Passed Checks</span>
              </div>
            </div>
          </div>

          {/* Core Grid Panels */}
          <div className="report-panels-grid">
            {/* Panel 1: Meta Tags */}
            <div className="report-panel-card p-meta">
              <div className="panel-card-header">
                <div className="panel-card-icon">🏷</div>
                <h4>Meta Tags & HTML Structure</h4>
                <span className="panel-badge-status warning">1 Warning</span>
              </div>
              <div className="panel-card-body">
                <div className="report-item-row passed">
                  <span className="status-check">✓</span>
                  <div className="item-details">
                    <h5>Title Tag <span className="tag-meta-value">(58 chars)</span></h5>
                    <p className="item-text">"Premium Organic Teas | Buy Handcrafted Blends Online"</p>
                  </div>
                </div>
                <div className="report-item-row passed">
                  <span className="status-check">✓</span>
                  <div className="item-details">
                    <h5>Meta Description <span className="tag-meta-value">(152 chars)</span></h5>
                    <p className="item-text">"Discover our curated selection of fine organic teas sourced directly from estate growers. Order online for fast nationwide shipping."</p>
                  </div>
                </div>
                <div className="report-item-row critical-issue">
                  <span className="status-cross">✗</span>
                  <div className="item-details">
                    <h5>Open Graph Image <span className="tag-meta-value">og:image</span></h5>
                    <p className="item-text text-danger">Missing og:image tag. Social shares will display a blank placeholder image instead of branding.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Panel 2: Broken Links */}
            <div className="report-panel-card p-links">
              <div className="panel-card-header">
                <div className="panel-card-icon">🔗</div>
                <h4>Links & Anchor Health</h4>
                <span className="panel-badge-status danger">1 Broken Link</span>
              </div>
              <div className="panel-card-body">
                <div className="links-stats-row">
                  <div className="l-stat"><span>38</span> Checked</div>
                  <div className="l-stat"><span>34</span> Internal</div>
                  <div className="l-stat text-danger font-bold"><span>1</span> Broken</div>
                </div>
                <table className="mock-links-table">
                  <thead>
                    <tr>
                      <th>Link Target</th>
                      <th>Type</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="link-url">/collections/herbal-teas</td>
                      <td>Internal</td>
                      <td><span className="badge badge-success">200 OK</span></td>
                    </tr>
                    <tr>
                      <td className="link-url">/blog/healthy-benefits-matcha</td>
                      <td>Internal</td>
                      <td><span className="badge badge-success">200 OK</span></td>
                    </tr>
                    <tr className="row-error">
                      <td className="link-url text-danger">/products/early-grey-special-discount</td>
                      <td>Internal</td>
                      <td><span className="badge badge-danger">404 Found</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Panel 3: Performance (PageSpeed) */}
            <div className="report-panel-card p-perf">
              <div className="panel-card-header">
                <div className="panel-card-icon">⚡</div>
                <h4>Core Web Vitals & Speed</h4>
                <span className="panel-badge-status warning">Needs Optimization</span>
              </div>
              <div className="panel-card-body">
                <div className="vitals-score-row">
                  <div className="vital-metric warning-text">
                    <span className="vital-val">3.2s</span>
                    <span className="vital-name">Largest Contentful Paint</span>
                    <span className="vital-bar"><span className="bar-fill warning" style={{ width: "70%" }}></span></span>
                  </div>
                  <div className="vital-metric success-text">
                    <span className="vital-val">15ms</span>
                    <span className="vital-name">First Input Delay</span>
                    <span className="vital-bar"><span className="bar-fill success" style={{ width: "95%" }}></span></span>
                  </div>
                  <div className="vital-metric success-text">
                    <span className="vital-val">0.02</span>
                    <span className="vital-name">Cumulative Layout Shift</span>
                    <span className="vital-bar"><span className="bar-fill success" style={{ width: "98%" }}></span></span>
                  </div>
                </div>
                <div className="report-item-row critical-issue">
                  <span className="status-cross">✗</span>
                  <div className="item-details">
                    <h5>Unoptimized Hero Image</h5>
                    <p className="item-text">"hero-matcha-banner.jpg" (1.8MB) delays LCP. Convert to WebP and apply modern responsive sizes.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Panel 4: Schema Markups */}
            <div className="report-panel-card p-schema">
              <div className="panel-card-header">
                <div className="panel-card-icon">✨</div>
                <h4>Schema & Structured Data</h4>
                <span className="panel-badge-status success">Valid Schema</span>
              </div>
              <div className="panel-card-body">
                <div className="schema-types-list">
                  <div className="schema-type-pill success">
                    <span className="pill-status">✓</span> Organization Schema
                  </div>
                  <div className="schema-type-pill success">
                    <span className="pill-status">✓</span> WebSite Schema
                  </div>
                  <div className="schema-type-pill warning">
                    <span className="pill-status">!</span> BreadcrumbList Schema
                  </div>
                </div>
                <div className="report-item-row warning-issue" style={{ marginTop: "1rem" }}>
                  <span className="status-excl">!</span>
                  <div className="item-details">
                    <h5>Missing author field in BreadcrumbList</h5>
                    <p className="item-text">Google Search Console might raise a non-critical notice. Recommended to inject default author field in sitemap config.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
